/**
 * Store Pinia des sectorCubicles (Composition API)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";

import { ISectorCubicle } from "../models/sectorCubicle"
import { ISectorCubicleApiResponse } from "../models/api/sectorCubicleApiResponse";
import { ISectorCubicleFilter } from "../models/filters/sectorCubicleFilter"
import SectorCubicleService from "../services/SectorCubicleService";
import { ISectorCubicleForm } from "../models/forms/sectorCubicleForm";
import { ITempSectorCubicleForm } from "../models/forms/tempSectorCubicleForm";

import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";

import { ICubicle } from "../models/cubicle";
import { ISector } from "../models/sector";

import { useSectorStore } from "./sector.store";
import { useCubicleStore } from "./cubicle.store";
import signalRManager from "../helper/signalRManager";
import i18n from "@/i18n";
import IPayloadStore from "../models/api/payloadStore";

/**
 * Store Pinia des sectorCubicles utilisant la Composition API
 */
export const useSectorCubicleStore = defineStore("sectorCubicles", () => {
    // State
    const datas = ref<ISectorCubicle[]>([]);
    const current = ref<ISectorCubicle>({} as ISectorCubicle);
    const itemForm = ref<ISectorCubicleForm>({} as ISectorCubicleForm);
    const arrayForm = ref<ISectorCubicleForm[]>([]);
    const tempArrayForm = ref<ITempSectorCubicleForm[]>([]);
    const isLoading = ref<boolean>(false);
    const buildingId = ref<number>(0);

    // Actions - Hydration

    /**
     * Hydrate SectorCubicles from API response
     * Loads sector and cubicle stores if needed and links entities by ID
     */
    async function hydrateSectorCubicles(sectorCubicles: ISectorCubicleApiResponse[]): Promise<ISectorCubicle[]> {
        if (sectorCubicles.length === 0) return [];

        // 1. Get stores
        const sectorStore = useSectorStore();
        const cubicleStore = useCubicleStore();

        // 2. Load in parallel if needed
        const loadPromises: Promise<unknown>[] = [];
        if (sectorStore.datas.length === 0) {
            loadPromises.push(sectorStore.load({ page: 1, pageSize: 0 }));
        }
        if (cubicleStore.datas.length === 0) {
            loadPromises.push(cubicleStore.load({ page: 1, pageSize: 0 }));
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Create Maps for O(1) lookups
        const sectorMap = new Map(sectorStore.datas.map(s => [s.id, s]));
        const cubicleMap = new Map(cubicleStore.datas.map(c => [c.id, c]));

        // 4. Hydrate all sectorCubicles in one pass
        const models: ISectorCubicle[] = [];
        for (const sc of sectorCubicles) {
            const model = { ...sc } as unknown as ISectorCubicle;

            // Link references (O(1) via Map)
            model.sector = sectorMap.get(sc.sectorId) ?? model.sector;
            model.cubicle = cubicleMap.get(sc.cubicleId) ?? model.cubicle;

            models.push(model);
        }

        return models;
    }

    // Getters

    /**
     * Get sectorCubicles for a specific building
     * Uses sectorId and buildingId instead of navigation properties
     */
    const getForBuilding = computed(() => {
        const sectorStore = useSectorStore();
        // Build Set of sector IDs for this building for O(1) lookup
        const sectorIdsInBuilding = new Set(
            sectorStore.datas
                .filter(s => s.buildingId === buildingId.value)
                .map(s => s.id)
        );

        return datas.value.filter(sc => sectorIdsInBuilding.has(sc.sectorId));
    });

    /**
     * MEDIUM Priority Optimization: Map of sectorCubicles grouped by cubicleId
     * Enables O(1) lookup instead of O(n) filter operations
     */
    const sectorsByCubicleId = computed(() => {
        const map = new Map<number, ISectorCubicle[]>();
        for (const sc of datas.value) {
            if (!sc.cubicleId) continue;
            let list = map.get(sc.cubicleId);
            if (!list) {
                list = [];
                map.set(sc.cubicleId, list);
            }
            list.push(sc);
        }
        return map;
    });

    // Actions

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     */
    async function load(filter: ISectorCubicleFilter) {
        isLoading.value = true;
        const result = await SectorCubicleService.getAll<ISectorCubicleFilter, ISectorCubicleApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateSectorCubicles(result.data as ISectorCubicleApiResponse[]);
        }
        return result;
    }

    /**
     * Récupération d'un secteurCubicle par son identifiant
     * @param id
     */
    async function get(id: number) {
        const result = await SectorCubicleService.getById<ISectorCubicleApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateSectorCubicles([result.data as ISectorCubicleApiResponse]);

            const index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0) {
                datas.value[index] = modelData;
            }

            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    /**
     * Récupération de l'objet dans le store
     * @param id Identifiant de l'objet
     */
    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    /**
     * Mise à jour ou création d'un objet
     * @param maj
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<ISectorCubicleApiResponse | IDataOperationResult<ISectorCubicleApiResponse>>;

        if (itemForm.value.id > 0) {
            result = await SectorCubicleService.update(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateSectorCubicles([result.data as ISectorCubicleApiResponse]);
                current.value = modelData;

                if (maj) {
                    const index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<ISectorCubicle>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await SectorCubicleService.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateSectorCubicles([result.data as ISectorCubicleApiResponse]);
                current.value = modelData;
            }
        }
        return result;
    }
    /**
     * Création en batch d'éléments en fonction de leurs données
     * @param items liste des objets à créer
     * @param maj
     */
    async function batchSave(items: ISectorCubicleForm[], maj: boolean = true) {
        const returnItems: ISectorCubicle[] = [];
        const createItems = items.filter(d => d.id == 0) as ISectorCubicleForm[];
        const updateItems = items.filter(d => d.id > 0) as ISectorCubicleForm[];

        if (createItems.length > 0) {
            const result = await SectorCubicleService.batchCreate(createItems);
            if (!result.error && maj) {
                const responseData = result.data as ISectorCubicleApiResponse[];
                const hydratedData = await hydrateSectorCubicles(responseData);
                hydratedData.forEach((item) => {
                    datas.value.push(item);
                    returnItems.push(item);
                });
            }
        }

        if (updateItems.length > 0) {
            const result = await SectorCubicleService.batchUpdate(updateItems);
            if (!result.error && maj) {
                const responseData = result.data as ISectorCubicleApiResponse[];
                const hydratedData = await hydrateSectorCubicles(responseData);
                hydratedData.forEach((item) => {
                    const index = datas.value.findIndex((a) => a.id == item.id);
                    if (index >= 0) {
                        datas.value[index] = item;
                        returnItems.push(item);
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<ISectorCubicle>>(true, { errors: error });
                    }
                });
            }
        }
        return returnItems;
    }

    /**
     * Delete de l'objet
     */
    async function deleteItem() {
        const result = await SectorCubicleService.delete<ISectorCubicle>(current.value.id, true);

        if (!result.error) {
            const index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem();
            itemForm.value = newForm();
        }
        return result;
    }

    async function batchDelete(ids: number[]) {
        const result = await SectorCubicleService.batchDelete(ids, true);
        if (!result.error) {
            datas.value = datas.value.filter(item => !ids.includes(item.id));
        }
        return result;
    }

    /**
     * Transformation de l'objet en formulaire
     * @param item SectorCubicle
     */
    function toForm(item: ISectorCubicle): ISectorCubicleForm {
        const form = {} as ISectorCubicleForm;
        Object.assign(form, item);

        // Use ID properties directly instead of navigation properties
        form.sectorId = item.sectorId;
        form.cubicleId = item.cubicleId;

        return form;
    }

    /**
     * Permet d'initialiser un objet vide pour le formulaire
     */
    function newForm(): ISectorCubicleForm {
        itemForm.value = {
            id: 0,
            sectorId: 0,
            cubicleId: 0,
        } as ISectorCubicleForm;
        return itemForm.value;
    }

    /**
     * Permet d'initialiser un objet vide pour les referentiels
     */
    function newItem(): ISectorCubicle {
        current.value = {
            id: 0,
            sectorId: 0,
            cubicleId: 0,
        } as ISectorCubicle;

        itemForm.value = toForm(current.value);
        return current.value;
    }

    function getTempSectorCubiclesByColor(sectorColor: string): ITempSectorCubicleForm[] {
        return tempArrayForm.value.filter(taf => taf.sectorColor == sectorColor);
    }

    return {
        // State
        datas,
        current,
        itemForm,
        arrayForm,
        tempArrayForm,
        isLoading,
        buildingId,
        // Getters
        getForBuilding,
        sectorsByCubicleId,
        // Actions
        hydrateSectorCubicles,
        load,
        get,
        searchStoreById,
        save,
        batchSave,
        delete: deleteItem,
        batchDelete,
        toForm,
        newForm,
        newItem,
        getTempSectorCubiclesByColor
    };
});

signalRManager.connection.on("MajSectorCubicle", async (payload: IPayloadStore) => {
    const store = useSectorCubicleStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateSectorCubicles([payload.data as ISectorCubicleApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateSectorCubicles([payload.data as ISectorCubicleApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
